import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, Phone, Sparkles, DollarSign, Home } from "lucide-react";
import { API } from "../constants";

const ConciergeQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: '',
    quantity: '',
    timeline: '',
    description: '',
    budget_range: '',
    special_requirements: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/concierge-quote`, formData);
      setSubmitted(true);
      toast.success('Your concierge quote request has been submitted! We will contact you within 24 hours.');
    } catch (error) {
      console.error('Error submitting concierge quote:', error);
      toast.error('Failed to submit quote request. Please try again.');
    }
    
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
      <SEO title="Get a Custom Quote" description="Get a personalized quote for your custom printing project. Tell us about your needs and we'll provide transparent pricing." path="/concierge-quote" />
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h1>
          <p className="text-xl text-gray-600 mb-8">
            We've received your concierge quote request and will contact you within 24 hours with a personalized proposal.
          </p>
          <Button onClick={() => window.location.href = '/'}>
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Concierge Quote Service</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Get a personalized quote with our white-glove service. Perfect for complex orders, custom requirements, or when you want expert guidance throughout your project.
        </p>
      </div>

      {/* Benefits Section */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Phone className="w-8 h-8 text-blue-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Personal Consultation</h3>
              <p className="text-sm text-gray-600">One-on-one guidance from our printing experts</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Sparkles className="w-8 h-8 text-purple-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Custom Solutions</h3>
              <p className="text-sm text-gray-600">Tailored recommendations for your specific needs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Best Pricing</h3>
              <p className="text-sm text-gray-600">Volume discounts and package deals for large orders</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quote Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Request Your Concierge Quote</CardTitle>
          <CardDescription>
            Tell us about your project and we'll create a personalized proposal with detailed pricing, timeline, and recommendations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="company">Company/Organization</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                />
              </div>
            </div>

            {/* Project Details */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project_type">Project Type *</Label>
                <Select value={formData.project_type} onValueChange={(value) => handleInputChange('project_type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apparel">Apparel Printing (T-shirts, Hoodies, etc.)</SelectItem>
                    <SelectItem value="embroidery">Custom Embroidery</SelectItem>
                    <SelectItem value="promotional">Promotional Products</SelectItem>
                    <SelectItem value="uniforms">Uniforms & Corporate Wear</SelectItem>
                    <SelectItem value="events">Event Merchandise</SelectItem>
                    <SelectItem value="mixed">Multiple Services</SelectItem>
                    <SelectItem value="other">Other (please describe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="quantity">Estimated Quantity</Label>
                <Input
                  id="quantity"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  placeholder="e.g., 100 pieces, 50-200 range"
                />
              </div>
              <div>
                <Label htmlFor="timeline">Timeline/Deadline</Label>
                <Input
                  id="timeline"
                  value={formData.timeline}
                  onChange={(e) => handleInputChange('timeline', e.target.value)}
                  placeholder="e.g., 2 weeks, by March 15th"
                />
              </div>
              <div>
                <Label htmlFor="budget_range">Budget Range (Optional)</Label>
                <Select value={formData.budget_range} onValueChange={(value) => handleInputChange('budget_range', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-500">Under $500</SelectItem>
                    <SelectItem value="500-1000">$500 - $1,000</SelectItem>
                    <SelectItem value="1000-2500">$1,000 - $2,500</SelectItem>
                    <SelectItem value="2500-5000">$2,500 - $5,000</SelectItem>
                    <SelectItem value="over-5000">Over $5,000</SelectItem>
                    <SelectItem value="prefer-not">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Project Description */}
            <div>
              <Label htmlFor="description">Project Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Tell us about your project, design ideas, target audience, and any specific requirements..."
                className="min-h-[120px]"
                required
              />
            </div>

            {/* Special Requirements */}
            <div>
              <Label htmlFor="special_requirements">Special Requirements or Questions</Label>
              <Textarea
                id="special_requirements"
                value={formData.special_requirements}
                onChange={(e) => handleInputChange('special_requirements', e.target.value)}
                placeholder="Any special printing methods, color matching, packaging, delivery, or other requirements..."
                className="min-h-[80px]"
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Request Concierge Quote'}
            </Button>

            <p className="text-sm text-gray-600 text-center">
              We'll review your request and contact you within 24 hours with a detailed proposal.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Customer Dashboard Component

export { ConciergeQuote };
export default ConciergeQuote;
